import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { EmptyState } from '../../components/EmptyState.js'
import { ErrorState } from '../../components/ErrorState.js'
import { LoadingState } from '../../components/LoadingState.js'
import { Topbar } from '../../components/Topbar.js'
import { Heatmap } from '../../components/charts/Heatmap.js'
import { LineChart } from '../../components/charts/LineChart.js'
import { Metric, trendDelta } from '../../components/Metric.js'
import { useAuth } from '../../lib/auth.js'
import { formatCount, formatRangeLabel, formatRate } from '../../lib/format.js'
import { PERIOD_OPTIONS, usePeriod } from '../../lib/period.js'
import { usePageTitle } from '../../lib/page-title.js'
import { ReviewTaskModal } from './ReviewTaskModal.js'
import { TASK_STATUS } from '@ovrsee/shared'
import { Button, Text } from '../../ui/index.js'

interface Summary {
  readonly avgEfficiency: number
  readonly avgComplexity: number
  readonly onTimeRate: number
  readonly returnedForCorrection: number
}

interface TrendPoint {
  readonly from: string
  readonly to: string
  readonly score: number | null
  readonly onTime: number | null
  readonly firstTimePass: number | null
}

interface TrendResponse {
  readonly points: readonly TrendPoint[]
}

interface Attention {
  readonly overdue: number
  readonly blocked: number
  readonly active: number
  readonly total: number
}

interface TaskListItem {
  readonly id: string
  readonly reference: string
  readonly title: string
  readonly assignee: { readonly id: string; readonly name: string } | null
  readonly effectiveDeadlineAt: string
}

interface TaskListPage {
  readonly items: readonly TaskListItem[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
}

interface PersonRow {
  readonly userId: string
  readonly name: string
  readonly departmentId: string | null
  readonly tasks: number
  readonly onTime: number | null
  readonly firstTimePass: number | null
  readonly avgComplexity: number
  readonly score: number | null
  readonly belowMinimumSample: boolean
}

interface ActivityItem {
  readonly id: string
  readonly type: string
  readonly createdAt: string
  readonly actor: { readonly id: string; readonly name: string } | null
  readonly task: { readonly id: string; readonly reference: string; readonly title: string }
}

function atLocalMidnight(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function addLocalDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days)
}

function shortDayLabel(date: Date): string {
  return `${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]} ${date.getDate()}`
}

function latestValue<T>(items: readonly T[], readValue: (item: T) => number | null | undefined): number | undefined {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    const value = readValue(items[index]!)
    if (value !== null && value !== undefined) return value
  }
  return undefined
}

function buildHeatmap(rows: readonly TaskListItem[], fromIso: string, toIso: string) {
  const from = new Date(fromIso)
  const to = new Date(toIso)
  const days: Date[] = []
  for (let cursor = new Date(from); cursor.getTime() < to.getTime(); cursor = addLocalDays(cursor, 1)) {
    days.push(new Date(cursor))
  }

  const columns = days.map((day) => shortDayLabel(day))
  const bucket = new Map<string, { readonly label: string; readonly counts: number[] }>()

  for (const task of rows) {
    const assigneeLabel = task.assignee?.name ?? 'Unassigned'
    const dayIso = atLocalMidnight(new Date(task.effectiveDeadlineAt)).toISOString()
    const columnIndex = days.findIndex((day) => atLocalMidnight(day).toISOString() === dayIso)
    if (columnIndex === -1) continue
    const current = bucket.get(assigneeLabel) ?? { label: assigneeLabel, counts: Array.from({ length: columns.length }, () => 0) }
    current.counts[columnIndex] = (current.counts[columnIndex] ?? 0) + 1
    bucket.set(assigneeLabel, current)
  }

  const rowsOut = [...bucket.values()].sort((a, b) => a.label.localeCompare(b.label))
  const data = rowsOut.map((row) => row.counts)
  const max = Math.max(1, ...data.flat())
  return {
    rows: rowsOut.map((row) => row.label),
    cols: columns,
    data,
    max,
  }
}

function sortPeople(rows: readonly PersonRow[]): PersonRow[] {
  return [...rows].sort((a, b) => {
    if (a.belowMinimumSample !== b.belowMinimumSample) return a.belowMinimumSample ? 1 : -1
    if (a.score === null && b.score === null) return a.name.localeCompare(b.name)
    if (a.score === null) return 1
    if (b.score === null) return -1
    if (a.score !== b.score) return b.score - a.score
    return a.name.localeCompare(b.name)
  })
}

function activityVerb(type: string): string {
  if (type === 'STATUS_CHANGED') return 'changed status on'
  if (type === 'REASSIGNED') return 'reassigned'
  if (type === 'DEADLINE_EXTENDED') return 'extended the deadline on'
  if (type === 'BLOCKED_FLAGGED') return 'flagged as blocked'
  if (type === 'CREATED') return 'created'
  return type.toLowerCase().replace(/_/g, ' ')
}

export function ManagerOverview() {
  usePageTitle('Overview')
  const { api } = useAuth()
  const { period, setPeriod, from, to, bucket } = usePeriod('quarter')
  const heatmapWindow = useMemo(() => {
    const now = atLocalMidnight(new Date())
    return {
      from: now.toISOString(),
      to: addLocalDays(now, 7).toISOString(),
    }
  }, [])
  const [reviewTaskId, setReviewTaskId] = useState<string | null>(null)

  const summaryQuery = useQuery({
    queryKey: ['overview-summary', from, to],
    queryFn: () => api.get<Summary>(`/api/analytics/summary?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  })

  const trendQuery = useQuery({
    queryKey: ['overview-trend', from, to, bucket],
    queryFn: () => api.get<TrendResponse>(`/api/analytics/trend?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&bucket=${bucket}`),
  })

  const attentionQuery = useQuery({
    queryKey: ['overview-attention'],
    queryFn: () => api.get<Attention>('/api/analytics/attention'),
  })

  const heatmapQuery = useQuery({
    queryKey: ['overview-heatmap', heatmapWindow.from, heatmapWindow.to],
    queryFn: () => api.get<TaskListPage>(`/api/tasks?from=${encodeURIComponent(heatmapWindow.from)}&to=${encodeURIComponent(heatmapWindow.to)}&pageSize=100`),
  })

  const reviewQuery = useQuery({
    queryKey: ['overview-review-queue'],
    queryFn: () => api.get<TaskListPage>(`/api/tasks?status=${TASK_STATUS.SUBMITTED}&pageSize=100`),
  })

  const peopleQuery = useQuery({
    queryKey: ['overview-people', from, to],
    queryFn: () => api.get<PersonRow[]>(`/api/analytics/people?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  })

  const activityQuery = useQuery({
    queryKey: ['overview-activity'],
    queryFn: () => api.get<readonly ActivityItem[]>('/api/activity?limit=20'),
  })

  const loading =
    summaryQuery.isPending ||
    trendQuery.isPending ||
    attentionQuery.isPending ||
    heatmapQuery.isPending ||
    reviewQuery.isPending ||
    peopleQuery.isPending ||
    activityQuery.isPending
  const failure =
    summaryQuery.error ??
    trendQuery.error ??
    attentionQuery.error ??
    heatmapQuery.error ??
    reviewQuery.error ??
    peopleQuery.error ??
    activityQuery.error
  const hasFailure =
    summaryQuery.isError ||
    trendQuery.isError ||
    attentionQuery.isError ||
    heatmapQuery.isError ||
    reviewQuery.isError ||
    peopleQuery.isError ||
    activityQuery.isError

  if (loading) {
    return (
      <>
        <Topbar title="Overview" eyebrow="Manager workspace" />
        <div className="page">
          <div className="page-section">
            <LoadingState label="Loading overview" />
          </div>
        </div>
      </>
    )
  }

  if (hasFailure) {
    return (
      <>
        <Topbar title="Overview" eyebrow="Manager workspace" />
        <div className="page">
          <div className="page-section">
            <ErrorState error={failure} onRetry={() => void summaryQuery.refetch()} />
          </div>
        </div>
      </>
    )
  }

  const summary = summaryQuery.data!
  const trend = trendQuery.data?.points ?? []
  const attention = attentionQuery.data!
  const reviewItems = reviewQuery.data?.items ?? []
  const people = sortPeople(peopleQuery.data ?? [])
  const activity = activityQuery.data ?? []
  const heatmap = buildHeatmap(heatmapQuery.data?.items ?? [], heatmapWindow.from, heatmapWindow.to)
  const topCoach = people.find((row) => row.score !== null && row.belowMinimumSample === false) ?? null
  const bottomCoach = [...people].reverse().find((row) => row.score !== null || row.belowMinimumSample) ?? null
  const efficiencyDelta = trendDelta(trend, (point) => point.score)
  const onTimeDelta = trendDelta(trend, (point) => point.onTime, 100)
  const firstTimeDelta = trendDelta(trend, (point) => point.firstTimePass, 100)

  return (
    <>
      <Topbar
        title="Overview"
        eyebrow="Manager workspace"
        actions={
          <div style={{ display: 'flex', gap: 'var(--o-space-2)', alignItems: 'center' }}>
            <div role="group" aria-label="Period selector" style={{ display: 'flex', gap: 8 }}>
              {PERIOD_OPTIONS.filter((option) => option.value === 'week' || option.value === 'month' || option.value === 'quarter').map((option) => (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={period === option.value}
                  onClick={() => setPeriod(option.value)}
                  style={{
                    border: '1px solid var(--border)',
                    borderRadius: 999,
                    padding: '6px 12px',
                    background: period === option.value ? 'var(--brand-soft)' : 'transparent',
                    color: period === option.value ? 'var(--mauve-700)' : 'var(--fg-muted)',
                    fontSize: 12.5,
                    fontWeight: 500,
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <Button kind="secondary" size="small">
              Export
            </Button>
          </div>
        }
      />

      <div className="page">
        <div className="page-section" style={{ display: 'grid', gap: 'var(--o-space-4)' }}>
          <div className="metric-grid">
            {/* Team efficiency: summary.avgEfficiency; trend score buckets drive the sparkline and delta. */}
            <Metric
              label="Team efficiency"
              value={formatCount(summary.avgEfficiency)}
              unit="/100"
              delta={efficiencyDelta}
              trend={trend.map((point) => point.score)}
            />
            {/* On-time rate: summary.onTimeRate; trend onTime buckets drive the sparkline and the pp delta. */}
            <Metric
              label="On-time rate"
              value={formatRate(summary.onTimeRate)}
              delta={onTimeDelta}
              deltaSuffix="pp"
              tone="pos"
              trend={trend.map((point) => point.onTime)}
            />
            {/* First-time satisfactory: summary fallback for the headline, trend firstTimePass drives the sparkline and delta. */}
            <Metric
              label="First-time satisfactory"
              value={formatRate(1 - summary.returnedForCorrection)}
              delta={firstTimeDelta}
              deltaSuffix="pp"
              tone="pos"
              trend={trend.map((point) => point.firstTimePass)}
            />
            {/* Needs attention is a task count, not a people count: overdue + blocked. */}
            <Metric
              label="Needs attention"
              value={formatCount(attention.overdue + attention.blocked)}
              unit="tasks"
              tone="warn"
              footnote={`${formatCount(attention.overdue)} overdue · ${formatCount(attention.blocked)} blocked`}
            />
          </div>

          <div className="split-grid split-grid--overview">
            <section style={{ display: 'grid', gap: 'var(--o-space-4)' }}>
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 'var(--o-space-4)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--o-space-3)' }}>
                  <div>
                    <div className="card-label">Performance trend</div>
                    <h3 style={{ margin: 0 }}>Three trend series, one quarter view</h3>
                  </div>
                  <Text color="secondary">{formatRangeLabel(from, to)}</Text>
                </div>
                <LineChart
                  width={640}
                  height={220}
                  yMax={100}
                  xLabels={trend.map((point) => shortDayLabel(new Date(point.from)))}
                  series={[
                    { label: 'Efficiency', data: trend.map((point) => point.score), color: 'var(--brand)', fill: true, dots: true },
                    { label: 'First-time satisfactory', data: trend.map((point) => point.firstTimePass), color: 'var(--positive)' },
                    { label: 'On-time', data: trend.map((point) => point.onTime), color: 'var(--info)' },
                  ]}
                />
                <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 11.5, color: 'var(--fg-muted)' }}>
                  <span>Score</span>
                  <span>First-time</span>
                  <span>On-time</span>
                </div>
              </div>

              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 'var(--o-space-4)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--o-space-3)' }}>
                  <div>
                    <div className="card-label">Workload heatmap</div>
                    <h3 style={{ margin: 0 }}>Next 7 days by assignee and deadline</h3>
                  </div>
                  <Text color="secondary">Grouped client-side from /tasks.</Text>
                </div>
                {/* The heatmap's fixed cells scroll inside the card on narrow viewports
                    rather than widening the page. */}
                <div style={{ overflowX: 'auto' }}>
                  <Heatmap rows={heatmap.rows} cols={heatmap.cols} data={heatmap.data} max={heatmap.max} />
                </div>
              </div>

              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
                <div style={{ padding: 'var(--o-space-4)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--divider)' }}>
                  <div>
                    <div className="card-label">Awaiting review</div>
                    <h3 style={{ margin: 0 }}>{formatCount(reviewItems.length)} submissions</h3>
                  </div>
                  <Text color="secondary">Click a row to open the existing review modal.</Text>
                </div>
                <div>
                  {reviewItems.length === 0 ? (
                    <div style={{ padding: 'var(--o-space-4)' }}>
                      <EmptyState title="No submissions to review" hint="The queue is empty right now." />
                    </div>
                  ) : (
                    reviewItems.map((task) => (
                      <button
                        key={task.id}
                        type="button"
                        onClick={() => setReviewTaskId(task.id)}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'minmax(0, 1fr) auto',
                          alignItems: 'center',
                          gap: 'var(--o-space-3)',
                          width: '100%',
                          padding: 'var(--o-space-3) var(--o-space-4)',
                          border: 'none',
                          borderTop: '1px solid var(--divider)',
                          background: 'transparent',
                          textAlign: 'left',
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 500 }}>{task.title}</div>
                          <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                            <span style={{ fontFamily: 'var(--font-mono)' }}>{task.reference}</span>
                            {task.assignee !== null ? <span> · {task.assignee.name}</span> : null}
                          </div>
                        </div>
                        <span style={{ color: 'var(--fg-muted)', fontSize: 12 }}>Review</span>
                      </button>
                    ))
                  )}
                </div>
                <ReviewTaskModal taskId={reviewTaskId} onClose={() => setReviewTaskId(null)} />
              </div>
            </section>

            <aside style={{ display: 'grid', gap: 'var(--o-space-4)' }}>
              <section style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 'var(--o-space-4)' }}>
                <div className="card-label">Coaching summary</div>
                <div style={{ display: 'grid', gap: 'var(--o-space-3)', marginTop: 'var(--o-space-3)' }}>
                  <div style={{ padding: 'var(--o-space-3)', borderRadius: 'var(--r-md)', background: 'var(--bg-subtle)' }}>
                    <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>Top ranked</div>
                    <div style={{ fontWeight: 500 }}>{topCoach?.name ?? 'No ranked people yet'}</div>
                    <Text color="secondary">
                      {topCoach !== null && topCoach.score !== null ? `${formatCount(topCoach.score)} /100` : 'Not enough data'}
                    </Text>
                  </div>
                  <div style={{ padding: 'var(--o-space-3)', borderRadius: 'var(--r-md)', background: 'var(--bg-subtle)' }}>
                    <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>Needs attention</div>
                    <div style={{ fontWeight: 500 }}>{bottomCoach?.name ?? 'No ranked people yet'}</div>
                    <Text color="secondary">
                      {bottomCoach !== null && bottomCoach.score !== null ? `${formatCount(bottomCoach.score)} /100` : 'Not enough data'}
                    </Text>
                  </div>
                </div>
              </section>

              <section style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 'var(--o-space-4)' }}>
                <div className="card-label">Activity feed</div>
                <div style={{ display: 'grid', gap: 'var(--o-space-2)', marginTop: 'var(--o-space-3)' }}>
                  {activity.map((item) => (
                    <div key={item.id} style={{ padding: 'var(--o-space-2) 0', borderTop: '1px solid var(--divider)', display: 'grid', gap: 4 }}>
                      <div style={{ fontSize: 12.5 }}>
                        <strong>{item.actor?.name ?? 'System'}</strong>{' '}
                        <span style={{ color: 'var(--fg-muted)' }}>
                          {activityVerb(item.type)} {item.task.reference} · {item.task.title}
                        </span>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--fg-subtle)', fontFamily: 'var(--font-mono)' }}>
                        {new Date(item.createdAt).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </aside>
          </div>
        </div>
      </div>
    </>
  )
}
